import React from 'react';
import { Table } from '../../components/ui/Table';
import { TableActionButton, TableActions } from '../../components/ui';
import { Eye } from 'lucide-react';
import { Badge } from '../../components/ui/Badge/Badge';
import { Modal } from '../../components/ui/Modal/Modal';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { apiFetch } from '@/lib/api';

interface LogRow {
  id: string;
  created_at: string;
  provider: string;
  model: string;
  purpose: string;
  endpoint: string;
  input_tokens: number;
  output_tokens: number;
  total_cost_usd: string;
  latency_ms: number;
  status: 'processing' | 'success' | 'error';
  error_code?: string;
  error_message?: string;
}

export function LLMLogs() {
  const [loading, setLoading] = React.useState(false);
  const [logs, setLogs] = React.useState<LogRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [totalItems, setTotalItems] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(15);
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [detail, setDetail] = React.useState<any | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const fetchLogs = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
        ...(filters.provider ? { provider: filters.provider } : {}),
        ...(filters.model ? { model: filters.model } : {}),
        ...(filters.status ? { status: filters.status } : {}),
      });
      const res = await apiFetch(`/api/admin/llm/logs?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load');
      setLogs(json.data || []);
      setTotalItems(json.pagination?.total || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, filters]);

  React.useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const columns = [
    {
      key: 'created_at',
      title: 'Time',
      render: (value: string) => new Date(value).toLocaleString(),
    },
    { key: 'provider', title: 'Provider' },
    { key: 'model', title: 'Model' },
    { key: 'purpose', title: 'Purpose' },
    { key: 'endpoint', title: 'Endpoint' },
    {
      key: 'status',
      title: 'Status',
      render: (value: 'processing' | 'success' | 'error') => (
        <Badge variant={value === 'success' ? 'success' : value === 'error' ? 'error' : 'warning'}>
          {value}
        </Badge>
      ),
    },
    { key: 'latency_ms', title: 'Latency (ms)', align: 'right' as const },
    {
      key: 'tokens',
      title: 'Tokens',
      render: (_: any, record: LogRow) => (
        <span>
          {record.input_tokens} / {record.output_tokens}
        </span>
      ),
      align: 'right' as const,
    },
    {
      key: 'total_cost_usd',
      title: 'Cost (USD)',
      render: (value: string) => `$${parseFloat(value || '0').toFixed(6)}`,
      align: 'right' as const,
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right' as const,
      render: (_: any, record: LogRow) => (
        <TableActions>
          <TableActionButton
            icon={Eye}
            onClick={async () => {
              setDetailLoading(true);
              try {
                const res = await fetch(`/api/admin/llm/logs/${record.id}`, { credentials: 'include' });
                const json = await res.json();
                if (json.success) setDetail(json.data);
              } finally {
                setDetailLoading(false);
              }
            }}
          >
            View
          </TableActionButton>
        </TableActions>
      )
    },
  ];

  const filterOptions = [
    {
      key: 'provider',
      label: 'Provider',
      options: [
        { label: 'Gemini', value: 'gemini' },
        { label: 'OpenAI', value: 'openai' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { label: 'Processing', value: 'processing' },
        { label: 'Success', value: 'success' },
        { label: 'Error', value: 'error' },
      ],
    },
    {
      key: 'model',
      label: 'Model',
      options: [
        { label: 'gemini-3.6-flash', value: 'gemini-3.6-flash' },
        { label: 'gemini-3.5-flash-lite', value: 'gemini-3.5-flash-lite' },
        { label: 'gemini-2.5-flash', value: 'gemini-2.5-flash' },
        { label: 'gemini-2.0-flash', value: 'gemini-2.0-flash' },
        { label: 'gpt-4o-mini', value: 'gpt-4o-mini' },
      ],
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="LLM Logs"
        description="Recent LLM calls with token usage, latency and cost."
      />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={logs}
        columns={columns}
        loading={loading}
        searchable={false}
        filters={filterOptions}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        emptyText="No logs"
        serverSide={true}
        onFilter={handleFilter}
        onPaginate={handlePaginate}
        totalItems={totalItems}
      />

      {detail && (
        <Modal
          isOpen={!!detail}
          onClose={() => setDetail(null)}
          title="LLM Call Details"
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Provider / Model</div>
                <div className="text-sm text-gray-900">{detail.provider} / {detail.model}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Purpose</div>
                <div className="text-sm text-gray-900">{detail.purpose}</div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Input</div>
              <div className="p-3 border rounded bg-gray-50 whitespace-pre-wrap text-sm max-h-72 overflow-auto">
                {detail.request_text || '—'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Output</div>
              <div className="p-3 border rounded bg-gray-50 whitespace-pre-wrap text-sm max-h-72 overflow-auto">
                {detail.response_text || '—'}
              </div>
            </div>

            {detail.status === 'error' && (detail.error_message || detail.error_code) && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-error-900 mb-1">Error Details</h4>
                    {detail.error_message && (
                      <p className="text-sm text-error-700 mb-2">{detail.error_message}</p>
                    )}
                    {detail.error_code && (
                      <p className="text-xs text-error-600">Code: {detail.error_code}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}


